import React, {Component} from 'react';
import RedactedChip from '../../../components/redacted-chip';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const DEFINITION_TYPES = ['VIEW', 'STORED_PROCEDURE', 'TRIGGER', 'FUNCTION'];

// eslint-disable-next-line security/detect-object-injection -- keys are STATUS literals from the diff engine
const STATUS_GLYPH = {ADDED: '+', REMOVED: '−', MODIFIED: '~', IDENTICAL: '='};
// eslint-disable-next-line security/detect-object-injection -- keys are STATUS literals from the diff engine
const STATUS_ARIA = {ADDED: 'added', REMOVED: 'removed', MODIFIED: 'modified', IDENTICAL: 'identical'};

const FIELD_LABEL_MAP = {
  dataType: 'type',
  maxLength: 'length',
  isNullable: 'nullable',
  defaultValue: 'default',
  columnKey: 'key',
  isUnique: 'unique',
  isUnsigned: 'unsigned',
  isZerofill: 'zerofill',
  isBinary: 'binary',
  isAutoIncrement: 'auto_increment',
  columnName: 'renamed',
};

const BOOL_FIELDS = new Set(['isUnique', 'isUnsigned', 'isZerofill', 'isBinary', 'isAutoIncrement']);

class DualPaneDiffView extends Component {
  constructor(props) {
    super(props);
    this.state = {expanded: {}};
  }

  // Flip the EFFECTIVE open state, not the raw slot: a MODIFIED table defaults
  // open with an undefined slot, so inverting the slot (!undefined === true) would
  // no-op the first click. Callers pass the current effective state.
  toggleObject = (name, currentlyExpanded) => {
    // eslint-disable-next-line security/detect-object-injection -- name is objectName from trusted diff result
    this.setState((prev) => ({expanded: {...prev.expanded, [name]: !currentlyExpanded}}));
  };

  // ponytail: MODIFIED default-open is derived at render time, no extra state needed
  isExpanded(obj) {
    const {expanded} = this.state;
    // eslint-disable-next-line security/detect-object-injection -- obj.objectName from trusted diff result
    return expanded[obj.objectName] !== undefined ? expanded[obj.objectName] : obj.status === 'MODIFIED';
  }

  fieldLabel(field) {
    // eslint-disable-next-line security/detect-object-injection -- field from fieldDiffs, trusted diff data
    return FIELD_LABEL_MAP[field] || field;
  }

  fieldValue(field, v) {
    if (v === null || v === undefined || v === '') return '—';
    if (BOOL_FIELDS.has(field)) {
      if (v === '1' || v === 1) return 'yes';
      if (v === '0' || v === 0) return 'no';
    }
    if (field === 'isNullable') {
      if (v === 'YES') return 'nullable';
      if (v === 'NO') return 'not null';
    }
    return String(v);
  }

  typeLabel(col) {
    if (!col) return '';
    // Reconstructed (saved-run) columns carry a pre-rendered label instead of
    // the raw column fields.
    if (col._label != null) return col._label;
    const parts = [col.dataType || ''];
    parts.push(col.isNullable === 'NO' ? 'NOT NULL' : 'NULL');
    if (col.defaultValue !== null && col.defaultValue !== undefined && col.defaultValue !== '') {
      parts.push(`DEFAULT ${col.defaultValue}`);
    }
    if (col.columnKey === 'PRI') parts.push('PK');
    if (col.isUnique === '1' || col.isUnique === 1) parts.push('UNIQUE');
    if (col.isAutoIncrement === '1' || col.isAutoIncrement === 1) parts.push('AUTO_INCREMENT');
    return parts.filter(Boolean).join(' ');
  }

  renderColumnTable(obj) {
    const {showOnlyDiffs} = this.props;
    const columnDiffs = obj.columnDiffs || [];

    const modified = columnDiffs.filter((cd) => cd.type === 'COLUMN_MODIFIED');
    const added = columnDiffs.filter((cd) => cd.type === 'COLUMN_ADDED');
    const removed = columnDiffs.filter((cd) => cd.type === 'COLUMN_REMOVED');

    const changedNames = new Set(columnDiffs.map((cd) => cd.columnName));
    const prevCols = (obj.previous && obj.previous.columns) ? obj.previous.columns : [];
    const identicalCols = prevCols.filter((c) => !changedNames.has(c.columnName));

    const tableRows = [];

    for (const cd of modified) {
      const fieldDiffs = cd.fieldDiffs || [];
      fieldDiffs.forEach((fd, i) => {
        tableRows.push(
            <TableRow
              key={`mod-${cd.columnName}-${fd.field}`}
              className="sc-row-modified"
              data-testid={i === 0 ? `sc-col-${obj.objectName}-${cd.columnName}` : undefined}
            >
              <TableCell>{i === 0 ? cd.columnName : ''}</TableCell>
              <TableCell>
                <span className="sc-fielddiff-label">{this.fieldLabel(fd.field)}: </span>
              </TableCell>
              <TableCell>{(fd.redactedFields || []).includes('old_value') ?
                <RedactedChip fields={fd.redactedFields} data-testid="sc-old-redacted" /> :
                <span className="sc-old">{this.fieldValue(fd.field, fd.oldValue)}</span>}</TableCell>
              <TableCell>{(fd.redactedFields || []).includes('new_value') ?
                <RedactedChip fields={fd.redactedFields} data-testid="sc-new-redacted" /> :
                <span className="sc-new">{this.fieldValue(fd.field, fd.newValue)}</span>}</TableCell>
            </TableRow>,
        );
      });
      if (fieldDiffs.length === 0) {
        tableRows.push(
            <TableRow
              key={`mod-${cd.columnName}-empty`}
              className="sc-row-modified"
              data-testid={`sc-col-${obj.objectName}-${cd.columnName}`}
            >
              <TableCell>{cd.columnName}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>,
        );
      }
    }

    for (const cd of added) {
      tableRows.push(
          <TableRow
            key={`add-${cd.columnName}`}
            className="sc-row-added"
            data-testid={`sc-col-${obj.objectName}-${cd.columnName}`}
          >
            <TableCell>{cd.columnName}</TableCell>
            <TableCell>added</TableCell>
            <TableCell>—</TableCell>
            <TableCell><span className="sc-new">{this.typeLabel(cd.new)}</span></TableCell>
          </TableRow>,
      );
    }

    for (const cd of removed) {
      tableRows.push(
          <TableRow
            key={`rem-${cd.columnName}`}
            className="sc-row-removed"
            data-testid={`sc-col-${obj.objectName}-${cd.columnName}`}
          >
            <TableCell>{cd.columnName}</TableCell>
            <TableCell>removed</TableCell>
            <TableCell><span className="sc-old">{this.typeLabel(cd.old)}</span></TableCell>
            <TableCell>—</TableCell>
          </TableRow>,
      );
    }

    const identicalSummary = (!showOnlyDiffs && identicalCols.length > 0) ? (() => {
      const MAX_NAMES = 8;
      const names = identicalCols.slice(0, MAX_NAMES).map((c) => c.columnName).join(', ');
      const suffix = identicalCols.length > MAX_NAMES ? ', …' : '';
      return (
        <div className="sc-tree-identical">
          {`${identicalCols.length} unchanged: ${names}${suffix}`}
        </div>
      );
    })() : null;

    return (
      <div className="sc-col-table-wrap">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Column</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Target</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows}
          </TableBody>
        </Table>
        {identicalSummary}
      </div>
    );
  }

  renderObjectHeader(obj) {
    const {onViewDefinitionDiff} = this.props;
    const isTable = obj.objectType === 'TABLE';
    const isDef = DEFINITION_TYPES.includes(obj.objectType);
    const isModified = obj.status === 'MODIFIED';
    // eslint-disable-next-line security/detect-object-injection -- STATUS_GLYPH keyed by known status literals
    const glyph = STATUS_GLYPH[obj.status] || '';
    // eslint-disable-next-line security/detect-object-injection -- STATUS_ARIA keyed by known status literals
    const ariaLabel = STATUS_ARIA[obj.status] || obj.status;

    const colDiffsCount = (obj.columnDiffs || []).length;
    // Total = union of source + target columns, so an added column isn't counted
    // out of the source-only total (which would read "4 of 5" when 5 excludes it).
    const prevCols = (obj.previous && obj.previous.columns) ? obj.previous.columns : [];
    const currCols = (obj.current && obj.current.columns) ? obj.current.columns : [];
    const totalCols = new Set([...prevCols.map((c) => c.columnName), ...currCols.map((c) => c.columnName)]).size;

    const showViewSqlDiff = isDef && isModified && !obj.definitionNote;

    return (
      <div
        className={`sc-tree-object sc-row-${obj.status.toLowerCase()}`}
        data-testid={`sc-object-row-${obj.objectName}`}
      >
        <span aria-label={ariaLabel} className="sc-status-glyph">{glyph}</span>
        <span className="sc-obj-name">{obj.objectName}</span>
        <span className="sc-obj-type">{obj.objectType}</span>
        <span className="sc-status-word">{obj.status.toLowerCase()}</span>
        {isTable && isModified && totalCols > 0 && (
          <span className="sc-col-hint">{`(${colDiffsCount} of ${totalCols} cols)`}</span>
        )}
        {isDef && isModified && obj.definitionNote && (
          <span className="sc-definition-note">{obj.definitionNote}</span>
        )}
        {showViewSqlDiff && (
          <Button
            size="small"
            onClick={() => onViewDefinitionDiff && onViewDefinitionDiff(obj)}
            data-testid={`sc-view-sqldiff-${obj.objectName}`}
          >
            View SQL diff
          </Button>
        )}
        {isTable && (
          <IconButton
            size="small"
            onClick={() => this.toggleObject(obj.objectName, this.isExpanded(obj))}
            data-testid={`sc-expand-${obj.objectName}`}
            aria-label={this.isExpanded(obj) ? 'collapse' : 'expand'}
          >
            {this.isExpanded(obj) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </div>
    );
  }

  render() {
    const {diff, showOnlyDiffs, statusFilter} = this.props;
    const objects = (diff && diff.objects) ? diff.objects : [];
    // An explicit stat-tile filter wins over the "only differences" toggle, so
    // clicking IDENTICAL still shows identical rows even with the toggle on.
    let visible;
    if (statusFilter) {
      visible = objects.filter((o) => o.status === statusFilter);
    } else if (showOnlyDiffs) {
      visible = objects.filter((o) => o.status !== 'IDENTICAL');
    } else {
      visible = objects;
    }

    return (
      <div className="sc-diff-tree" data-testid="sc-dual-pane">
        {visible.map((obj) => (
          <div key={obj.objectName}>
            {this.renderObjectHeader(obj)}
            {obj.objectType === 'TABLE' && this.isExpanded(obj) && this.renderColumnTable(obj)}
          </div>
        ))}
      </div>
    );
  }
}

export default DualPaneDiffView;
